import {
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useCreateCrmBrandContactDetailsApi } from 'apis/crm-brand-contact-details';
import React, { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

interface CreateContactDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  brand: { value: number; label: string } | null;
  contactName: string;
  areaName: string;
  designation: string;
  phoneNumber: string;
  email: string;
}

const defaultValues: FormData = {
  brand: null,
  contactName: '',
  areaName: '',
  designation: '',
  phoneNumber: '',
  email: '',
};

export const CreateContactDetailsModal: React.FC<CreateContactDetailsModalProps> = ({ isOpen, onClose }) => {
  // Self-contained mutation with success handling
  const { mutate: createContactDetails, isLoading: isCreating } = useCreateCrmBrandContactDetailsApi({
    onSuccess: () => {
      onCloseModal();
    },
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
    }
  }, [isOpen, reset]);

  // Memoized close handler
  const onCloseModal = useCallback(() => {
    reset(defaultValues);
    onClose();
  }, [reset, onClose]);

  // Async load brands with search functionality
  const loadBrandOptions = useCallback(async (inputValue: string) => {
    try {
      const { data } = await http.get('ums/v1/crm/management/brands', {
        params: {
          page: 1,
          limit: 50,
          name: inputValue || undefined,
        },
      });
      return (data?.data || []).map((brand: any) => ({
        value: brand.id,
        label: brand.name,
      }));
    } catch (error) {
      console.error('Error loading brands:', error);
      return [];
    }
  }, []);

  // Memoized submit handler
  const onSubmit = useCallback(
    (data: FormData) => {
      if (!data.brand?.value) return;

      const payload = {
        brandId: data.brand.value,
        contactName: data.contactName.trim(),
        areaName: data.areaName.trim() || undefined,
        designation: data.designation.trim() || undefined,
        phoneNumber: data.phoneNumber.trim() || undefined,
        email: data.email.trim() || undefined,
      };

      createContactDetails(payload);
    },
    [createContactDetails]
  );

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size='lg' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add New Contact Details</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} as='form' onSubmit={handleSubmit(onSubmit)}>
            <FormControl isRequired isInvalid={!!errors.brand}>
              <FormLabel>Brand</FormLabel>
              <Controller
                name='brand'
                control={control}
                rules={{ required: 'Brand is required' }}
                render={({ field }) => (
                  <ChakraAsyncSelect
                    {...field}
                    loadOptions={loadBrandOptions}
                    placeholder='Search and select a brand...'
                    isClearable
                    defaultOptions
                    cacheOptions
                    loadingMessage={() => 'Searching brands...'}
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? `No brands found for "${inputValue}"` : 'Type to search brands'
                    }
                  />
                )}
              />
              <FormErrorMessage>{errors.brand?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.contactName}>
              <FormLabel>Contact Name</FormLabel>
              <Input
                {...register('contactName', {
                  required: 'Contact name is required',
                  minLength: { value: 2, message: 'Contact name must be at least 2 characters' },
                })}
                placeholder='Enter contact name'
              />
              <FormErrorMessage>{errors.contactName?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.areaName}>
              <FormLabel>Area Name</FormLabel>
              <Input
                {...register('areaName', {
                  minLength: { value: 2, message: 'Area name must be at least 2 characters' },
                })}
                placeholder='Enter area name (optional)'
              />
              <FormErrorMessage>{errors.areaName?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.designation}>
              <FormLabel>Designation</FormLabel>
              <Input
                {...register('designation', {
                  minLength: { value: 2, message: 'Designation must be at least 2 characters' },
                })}
                placeholder='Enter designation (optional)'
              />
              <FormErrorMessage>{errors.designation?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.phoneNumber}>
              <FormLabel>Phone Number</FormLabel>
              <Input
                {...register('phoneNumber', {
                  pattern: {
                    value: /^[0-9()\-\s+]+$/,
                    message: 'Invalid phone number format',
                  },
                })}
                placeholder='Enter phone number (optional)'
              />
              <FormErrorMessage>{errors.phoneNumber?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.email}>
              <FormLabel>Email</FormLabel>
              <Input
                type='email'
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email format',
                  },
                })}
                placeholder='Enter email (optional)'
              />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant='outline' onClick={onCloseModal}>
              Cancel
            </Button>
            <Button
              colorScheme='blue'
              onClick={handleSubmit(onSubmit)}
              isLoading={isCreating}
              loadingText='Creating...'>
              Create Contact Details
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
